import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ILicenseBackOffice,
    ILicenseFrontOffice,
    ILicenseUsageData,
    Interval,
    LicenseOfficeType,
} from '@models';
import { aggregateLicenseUsage } from '@pages/shared/chart.config';
import { ODataQueryOptions } from '@services/generics/odata.query';
import { ODataService } from '@services/generics/odata.service';
import { MapperService } from '@services/mapper.service';

import { map } from 'rxjs/operators';

export enum LicenseUrlMap {
    unattended = 'Unattended',
    nonproduction = 'NonProduction',
    development = 'Development',
    studiox = 'StudioX',
    attended = 'Attended',
}
export enum LicenseUrlMapBackOffice {
    unattended = LicenseUrlMap.unattended,
    nonproduction = LicenseUrlMap.nonproduction,
}

export enum LicenseUrlMapFrontOffice {
    development = LicenseUrlMap.development,
    studiox = LicenseUrlMap.studiox,
    attended = LicenseUrlMap.attended,
}

export const licenseOfficeType = (licenseType: LicenseUrlMap): LicenseOfficeType => {
    switch (licenseType) {

        case LicenseUrlMap.unattended:
        case LicenseUrlMap.nonproduction:
            return LicenseOfficeType.BackOffice;

        case LicenseUrlMap.development:
        case LicenseUrlMap.studiox:
        case LicenseUrlMap.attended:
            return LicenseOfficeType.FrontOffice;

        default:
            throw new Error('Unknown licenseType');
    }
};

const generateRuntimeEndpoint = (type: LicenseUrlMapBackOffice) =>
    `/odata/LicensesRuntime/UiPath.Server.Configuration.OData.GetLicensesRuntime(robotType='${type}')`;
const generateNamedUserEndpoint = (type: LicenseUrlMapFrontOffice) =>
    `/odata/LicensesNamedUser/UiPath.Server.Configuration.OData.GetLicensesNamedUser(robotType='${type}')`;

@Injectable()
export class RuntimeLicenseService extends ODataService<ILicenseBackOffice, number> {
    protected _endpoint = '';
    constructor(http: HttpClient) {
        super(http, MapperService.runtimeLicenseMap);
    }
    public getLicenseDetails(licenseType: LicenseUrlMapBackOffice, queryOptions: ODataQueryOptions) {
        this._endpoint = generateRuntimeEndpoint(licenseType);
        return this.get(queryOptions);
    }

    public toggleEnabled(license: ILicenseBackOffice, licenseType: LicenseUrlMapBackOffice) {
        const data = {
            'key': license.key,
            'robotType': licenseType,
            'enabled': license.enabled,
        };

        return this._http.post(
            `/odata/LicensesRuntime('${license.machineName}')/UiPath.Server.Configuration.OData.ToggleEnabled`,
            JSON.stringify(data),
        );
    }
}

@Injectable()
export class NamedUserLicenseService extends ODataService<ILicenseFrontOffice, number> {
    protected _endpoint = '';
    constructor(http: HttpClient) {
        super(http, MapperService.namedLicenseMap);
    }
    public getLicenseDetails(licenseType: LicenseUrlMapFrontOffice, queryOptions: ODataQueryOptions) {
        this._endpoint = generateNamedUserEndpoint(licenseType);
        return this.get(queryOptions);
    }
}

@Injectable()
export class LicenseUsageService {
    protected _endpoint = '/api/stats';
    constructor(private _http: HttpClient) { }

    public getTenantStatsById(tenantId: number, daysNo: Interval) {
        return this._http
            .get(`${this._endpoint}/GetLicenseStats?tenantId=${tenantId}&days=${daysNo}`)
            .pipe(
                map(res => aggregateLicenseUsage(res as ILicenseUsageData[], daysNo)),
            );
    }
}
